import React, { useState, useCallback, useRef, type ChangeEvent } from 'react';

import { Select, Input, Spin } from 'antd';
import { useTranslation } from 'react-i18next';

const { TextArea } = Input;

interface SelectOption {
  label: string;
  value: string | number;
  [key: string]: unknown;
}

interface LazyLoadResponse {
  success?: boolean;
  data?: Array<{ id: string; name: string; [key: string]: unknown }>;
  has_more?: boolean;
}

interface LazyLoadConfig {
  action: (params: Record<string, unknown>) => Promise<LazyLoadResponse>;
  transform?: (payload: { data: LazyLoadResponse['data'] }) => SelectOption[];
}

interface MultipleValueProps {
  inputName: string;
  value?: any;
  onChange?: (val: any) => void;
  defaultOptions?: SelectOption[];
  createable?: boolean;
  lazyLoad?: LazyLoadConfig | null;
}

const MultipleValue: React.FC<MultipleValueProps> = ({
  inputName,
  value,
  onChange,
  defaultOptions = [],
  createable = false,
  lazyLoad = null,
}) => {
  const { t } = useTranslation();

  // ✅ 只在 lazyLoad 時維護內部 state
  const [lazyLoadedOptions, setLazyLoadedOptions] = useState<SelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [lastId, setLastId] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);

  const searchDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // 延遲載入資料
  const loadOptions = async (reset = false, search = '') => {
    if (!lazyLoad || !lazyLoad.action || loading) return;
    if (!reset && !hasMore) return;

    setLoading(true);
    try {
      const params: Record<string, unknown> = { page_size: 20 };
      if (!reset && lastId) {
        params.since_id = lastId;
      }
      if (search) {
        params.search = search;
      }

      const data = await lazyLoad.action(params);

      if (data && data.success) {
        const list = data.data || [];

        // 更新 lastId
        if (list.length > 0) {
          setLastId(list[list.length - 1].id);
        }

        // 檢查是否還有更多數據
        const newHasMore = data.has_more !== undefined ? data.has_more : (list.length >= (params.page_size as number));

        let transformedOptions: SelectOption[] = [];
        if (lazyLoad.transform) {
          transformedOptions = lazyLoad.transform({ data: list });
        } else {
          transformedOptions = list.map((item) => ({ label: item.name, value: item.id }));
        }

        setLazyLoadedOptions((prev) => reset ? transformedOptions : [...prev, ...transformedOptions]);
        setHasMore(newHasMore);
        setHasLoaded(true);
      }
    } catch (err) {
      console.log(`Failed to load options:`, err);
      if (reset) setLazyLoadedOptions([]);
    } finally {
      setLoading(false);
    }
  };

  // ✅ 根據場景選擇 options 來源（不需要 useEffect 同步）
  const options = lazyLoad ? lazyLoadedOptions : defaultOptions;

  const handleSelectChange = (val: any) => {
    // 創建隱藏的 input 來保持表單提交的兼容性
    const form = document.querySelector('form');
    if (form) {
      // 移除舊的隱藏 input
      const existingInputs = form.querySelectorAll(`input[name="${inputName}"]`);
      existingInputs.forEach((input) => input.remove());

      // 為每個選中的值創建隱藏 input
      if (val && val.length > 0) {
        val.forEach((v: string | number) => {
          const hiddenInput = document.createElement('input');
          hiddenInput.type = 'hidden';
          hiddenInput.name = inputName;
          hiddenInput.value = String(v);
          form.appendChild(hiddenInput);
        });
      }
    }

    if (onChange) {
      onChange(val);
    }
  };

  // 當下拉框被打開時載入資料
  const handleDropdownVisibleChange = (open: boolean) => {
    if (open && lazyLoad && !hasLoaded) {
      loadOptions(true); // Reset load
    }
  };

  // 處理搜尋（有 lazyLoad 時走後端搜尋）
  const handleSearch = useCallback((input: string) => {
    if (!lazyLoad) return;

    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }

    searchDebounceRef.current = setTimeout(() => {
      setLastId(null);
      setHasMore(true);
      loadOptions(true, input);
    }, 800);
  }, [lazyLoad, loading]);

  // 處理滾動加載
  const handlePopupScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    if (target.scrollTop + target.offsetHeight === target.scrollHeight) {
      if (hasMore && !loading) {
        loadOptions(false); // Load next
      }
    }
  };

  if (createable) {
    return (
      <TextArea
        name={inputName}
        value={value}
        onChange={onChange as (e: ChangeEvent<HTMLTextAreaElement>) => void}
        placeholder={t('common.table.filters.multiple_value_textarea_placeholder')}
        rows={3}
        style={{ width: '100%' }}
      />
    );
  }
  return (
    <Select
      mode="multiple"
      allowClear
      placeholder={t('common.table.filters.multiple_value_placeholder')}
      value={value}
      onChange={handleSelectChange}
      onOpenChange={handleDropdownVisibleChange}
      onPopupScroll={handlePopupScroll}
      onSearch={lazyLoad ? handleSearch : undefined}
      options={options}
      style={{ width: '100%' }}
      showSearch
      loading={loading}
      notFoundContent={loading ? <Spin size="small" /> : t('common.no_data')}
      filterOption={lazyLoad
        ? false
        : (input, option) => ((option?.label as string) ?? '').toLowerCase().includes(input.toLowerCase())
      }
    />
  );
};

export default MultipleValue;
